// Infrastructure dashboard — staff-only view of Squish's running infra.
//
// Data source: squish-backend's capacity-snapshot cron parks one doc in
// Firestore every 4 hours (lib/capacity-snapshots.js → infrastructure_state/
// current). squish-crm-backend proxies that snapshot to /api/infrastructure/
// current. This page renders 3 MVP cards from it.
//
// Cards (MVP):
//   1. Stripe MRR — total + at-risk subs (past_due/unpaid)
//   2. DigitalOcean droplets — fleet count + monthly spend
//   3. Plesk shared servers — utilisation per server, warn near capacity
//   4. Unclaimed — sites and droplets on our servers that no production
//      customer record claims (squish-backend lib/unclaimed-inventory.js).
//      Report only; teardown stays a human decision made by name.
//
// The Stack tab (StackMap.jsx) maps every provider and service by layer.
//
// Phase-2 polish (task #32): trend charts from /history, per-region/per-size
// breakdowns, drill-into-droplet, Plesk capacity-planning panel.

import React, { useEffect, useState, useCallback } from 'react';
import { getHeaders } from '../api';
import StackMap from './StackMap';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

function formatCurrency(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatAge(ms) {
  if (ms == null) return '—';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─── Card: Stripe MRR ────────────────────────────────────────────────────
function MrrCard({ stripeMrr }) {
  if (stripeMrr?.error) {
    return (
      <div className="stat-card">
        <div className="stat-label">MRR — Stripe</div>
        <div className="stat-value" style={{ fontSize: 18, color: '#f87171' }}>Snapshot error</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>{stripeMrr.error}</div>
      </div>
    );
  }
  const total = stripeMrr?.totalMrrUsd ?? 0;
  const activeCount = stripeMrr?.totalActive ?? 0;
  const atRisk = stripeMrr?.atRisk || {};
  const atRiskTotal = (atRisk.pastDue || 0) + (atRisk.unpaid || 0);
  const byKind = stripeMrr?.byKind || {};

  return (
    <div className="stat-card">
      <div className="stat-label">MRR — Stripe</div>
      <div className="stat-value">{formatCurrency(total)}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
        {activeCount} active subs
        {atRiskTotal > 0 && (
          <span style={{ color: '#f59e0b', marginLeft: 8 }}>
            · {atRiskTotal} at-risk ({atRisk.pastDue || 0} past-due, {atRisk.unpaid || 0} unpaid)
          </span>
        )}
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        {Object.entries(byKind)
          .filter(([, v]) => v?.count > 0)
          .sort(([, a], [, b]) => (b.mrrUsd || 0) - (a.mrrUsd || 0))
          .map(([kind, v]) => (
            <div key={kind} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--text2)' }}>{kind}</span>
              <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(v.mrrUsd)} <span style={{ color: 'var(--text3)' }}>({v.count})</span>
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─── Card: DO fleet ──────────────────────────────────────────────────────
function FleetCard({ doFleet }) {
  if (doFleet?.error) {
    return (
      <div className="stat-card">
        <div className="stat-label">DigitalOcean fleet</div>
        <div className="stat-value" style={{ fontSize: 18, color: '#f87171' }}>Snapshot error</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>{doFleet.error}</div>
      </div>
    );
  }
  const total = doFleet?.total ?? 0;
  const spend = doFleet?.monthlySpendUsd ?? 0;
  const byRegion = doFleet?.byRegion || {};
  const byStatus = doFleet?.byStatus || {};
  const nonActive = Object.entries(byStatus).filter(([s]) => s !== 'active');

  return (
    <div className="stat-card">
      <div className="stat-label">DigitalOcean fleet</div>
      <div className="stat-value">{total}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
        {formatCurrency(spend)}/mo on DO
        {nonActive.length > 0 && (
          <span style={{ color: '#f59e0b', marginLeft: 8 }}>
            · {nonActive.map(([s, n]) => `${n} ${s}`).join(', ')}
          </span>
        )}
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>By region</div>
        {Object.entries(byRegion)
          .sort(([, a], [, b]) => b - a)
          .map(([region, n]) => (
            <div key={region} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--text2)', fontFamily: 'var(--mono)' }}>{region}</span>
              <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            </div>
          ))}
        {Object.keys(byRegion).length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>No droplets yet</div>
        )}
      </div>
    </div>
  );
}

// ─── Card: Plesk shared ──────────────────────────────────────────────────
function PleskCard({ pleskShared }) {
  if (pleskShared?.error) {
    return (
      <div className="stat-card">
        <div className="stat-label">Plesk shared servers</div>
        <div className="stat-value" style={{ fontSize: 18, color: '#f87171' }}>Snapshot error</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>{pleskShared.error}</div>
      </div>
    );
  }
  const total = pleskShared?.totalSites ?? 0;
  const servers = pleskShared?.servers || [];

  // Surface 85%+ utilisation up front so the eye catches near-full servers
  // before scanning the per-server list.
  const nearFull = servers.filter(s => (s.utilisationPct || 0) >= 85);

  return (
    <div className="stat-card">
      <div className="stat-label">Plesk shared servers</div>
      <div className="stat-value">{total}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
        sites across {servers.length} server{servers.length === 1 ? '' : 's'}
        {nearFull.length > 0 && (
          <span style={{ color: '#f59e0b', marginLeft: 8 }}>
            · {nearFull.length} near capacity
          </span>
        )}
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        {servers.map(s => {
          const pct = s.utilisationPct ?? 0;
          const warn = pct >= 85;
          return (
            <div key={s.serverId} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: 'var(--text2)', fontFamily: 'var(--mono)' }}>{s.serverId}</span>
                <span style={{ color: warn ? '#f59e0b' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {s.activeCount}{s.capacity ? `/${s.capacity}` : ''} {s.capacity ? `· ${pct}%` : ''}
                </span>
              </div>
              {s.capacity != null && (
                <div style={{ height: 4, background: 'var(--bg)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(pct, 100)}%`,
                    height: '100%',
                    background: warn ? '#f59e0b' : 'var(--accent)',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              )}
            </div>
          );
        })}
        {servers.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>No servers configured</div>
        )}
      </div>
    </div>
  );
}

// ─── Card: Unclaimed ─────────────────────────────────────────────────────
// Anything on our servers that no production customer record claims, plus
// records that say something should already be gone. A read error is shown as
// such: an unreadable box must never look like a clean one.
const UNCLAIMED_REASONS = {
  'no-record': 'No customer record',
  'record-deleted': 'Record says deleted',
  'past-cleanup-date': 'Past cleanup date',
  'keep-list-review-due': 'Keep-list review due',
};

function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return null;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

function UnclaimedCard({ unclaimed }) {
  if (!unclaimed) return null;   // backend not reporting it yet
  // Two shapes arrive here: the full list doc ({ items, errors: [...], counts })
  // or, if that doc couldn't be read, the snapshot's counts-only summary
  // ({ total, byKind, byReason, errors: <number> }).
  const items = Array.isArray(unclaimed.items) ? unclaimed.items : [];
  const errors = Array.isArray(unclaimed.errors) ? unclaimed.errors : [];
  const errorCount = Array.isArray(unclaimed.errors) ? errors.length : Number(unclaimed.errors) || 0;
  const total = unclaimed.total ?? unclaimed.counts?.total ?? items.length;
  const hasErrors = errorCount > 0;
  // With a read error the count is only a floor. A bare "0" there would read
  // as a clean fleet, which is the one thing this card must never say falsely.
  const headline = hasErrors ? (total > 0 ? `${total}+` : '—') : total;

  return (
    <div className="stat-card" style={{ marginTop: 16 }}>
      <div className="stat-label">Unclaimed on our servers</div>
      <div className="stat-value" style={{ color: total > 0 || hasErrors ? '#f59e0b' : undefined }}>{headline}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
        Sites and droplets no customer record claims. Report only — nothing is deleted automatically.
      </div>

      {hasErrors && (
        <div style={{ marginTop: 12 }}>
          {errors.map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: '#f59e0b', marginBottom: 4 }}>
              ⚠ Couldn't read {e.serverId || e.source}: <span style={{ color: 'var(--text3)' }}>{e.error}</span>
            </div>
          ))}
          {errors.length === 0 && (
            <div style={{ fontSize: 12, color: '#f59e0b' }}>⚠ {errorCount} source{errorCount === 1 ? '' : 's'} couldn't be read.</div>
          )}
        </div>
      )}
      {items.length === 0 && total > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>The list itself couldn't be loaded; this is the count from the last snapshot.</div>
      )}

      {items.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          {items.map(it => {
            const size = it.kind === 'droplet'
              ? (it.monthlyUsd != null ? `${formatCurrency(it.monthlyUsd)}/mo` : null)
              : formatBytes(it.diskUsedBytes);
            const where = it.kind === 'droplet' ? 'droplet' : it.serverId;
            return (
              <div key={`${it.kind}:${it.name}`} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text)', fontFamily: 'var(--mono)', overflowWrap: 'anywhere' }}>{it.name}</span>
                  <span style={{ color: 'var(--text2)', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: '#f59e0b' }}>{UNCLAIMED_REASONS[it.reason] || it.reason}</span>
                    {it.ageDays != null && ` · ${it.ageDays}d old`}
                    {size && ` · ${size}`}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  <span style={{ fontFamily: 'var(--mono)' }}>{where}</span> · {it.detail}
                  {it.readError && ` (details unreadable: ${it.readError})`}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total === 0 && !hasErrors && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>Everything on our servers is claimed.</div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'fleet', label: 'Fleet' },
  { key: 'stack', label: 'Stack' },
];
const TAB_KEY = 'crm.infrastructure.tab';

function initialTab() {
  try {
    const saved = localStorage.getItem(TAB_KEY);
    if (TABS.some(t => t.key === saved)) return saved;
  } catch { /* storage blocked: default tab */ }
  return 'fleet';
}

export default function Infrastructure() {
  const [tab, setTab] = useState(initialTab);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/infrastructure/current`, { headers: getHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Failed to load snapshot (${res.status})`);
      setSnapshot(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickTab = key => {
    setTab(key);
    try { localStorage.setItem(TAB_KEY, key); } catch { /* not remembered */ }
  };

  const ageMs = snapshot?.meta?.ageMs;
  const isStale = snapshot?.meta?.isStale;

  return (
    <div className="page-content">
      <h1 style={{ margin: '0 0 8px' }}>Infrastructure</h1>
      <p style={{ color: 'var(--text2)', fontSize: 13, margin: '0 0 16px' }}>
        {tab === 'stack'
          ? 'Every outside provider and every service we run, grouped by layer.'
          : 'Snapshot of running Squish infrastructure — DO fleet, Plesk shared servers, Stripe MRR, and anything on our servers no customer record claims. Refreshed every 4 hours by the capacity-snapshot cron.'}
      </p>

      <div className="tab-row">
        <div className="tab-bar">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => pickTab(t.key)}
              className={`tab-btn${tab === t.key ? ' is-active' : ''}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'fleet' && (
          <button className="tab-refresh" onClick={load} disabled={loading}>
            {loading ? 'loading…' : 'refresh'}
          </button>
        )}
      </div>

      {tab === 'stack' && <StackMap />}

      {tab === 'fleet' && (<>
      {snapshot && (
        <div style={{
          marginBottom: 24,
          padding: '8px 12px',
          fontSize: 12,
          color: isStale ? '#f59e0b' : 'var(--text3)',
          background: isStale ? 'rgba(245,158,11,0.08)' : 'transparent',
          border: `1px solid ${isStale ? 'rgba(245,158,11,0.25)' : 'var(--border)'}`,
          borderRadius: 6,
        }}>
          {isStale ? '⚠ ' : ''}Snapshot from {formatAge(ageMs)}
          {isStale && ' — the cron may have skipped. Check the GitHub Actions workflow.'}
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 14px',
          marginBottom: 20,
          background: 'rgba(248,113,113,0.08)',
          border: '1px solid rgba(248,113,113,0.25)',
          borderRadius: 8,
          color: '#fca5a5',
          fontSize: 13,
        }}>
          {error}
          {error.includes('No infrastructure snapshot') && (
            <div style={{ fontSize: 11, marginTop: 6, color: 'var(--text3)' }}>
              First-time deploy? Trigger <span style={{ fontFamily: 'var(--mono)' }}>.github/workflows/capacity-snapshots.yml</span> manually
              on squish-backend (Actions tab → Run workflow).
            </div>
          )}
        </div>
      )}

      {loading && !snapshot && (
        <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading snapshot…</div>
      )}

      {snapshot && (
        <div className="stat-grid">
          <MrrCard stripeMrr={snapshot.stripeMrr} />
          <FleetCard doFleet={snapshot.doFleet} />
          <PleskCard pleskShared={snapshot.pleskShared} />
        </div>
      )}
      {snapshot && <UnclaimedCard unclaimed={snapshot.unclaimed} />}
      </>)}
    </div>
  );
}
